"use client";
import { apiGateway } from "@/_old/services/api-gateway";
import { buildQueryString, productTypeIds } from "@/core/utils";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { STEPS } from "../consts/steps";
import { ReportData, ReportExportButtonProps } from "../types";

async function fetchReportData(
  filters: ReportExportButtonProps["filters"],
): Promise<ReportData> {
  const flat = {
    start_date: filters.startDate,
    end_date: filters.endDate,
    company_id: filters.companyId ?? null,
    group_id: filters.group_id ?? null,
    status: filters.status ?? null,
    types:
      (filters.type &&
        filters.type.length > 0 &&
        filters.type.map((t) => productTypeIds[t])) ||
      null,
    cities:
      (filters.cities &&
        filters.cities.length > 0 &&
        filters.cities.map((c) => c.name)) ||
      null,
    neighborhoods: filters.neighborhoods ?? null,
    users:
      (filters.users &&
        filters.users.length > 0 &&
        filters.users
          .map((u) => u.user?.id)
          .filter((id): id is number => Boolean(id))) ||
      null,
  } as const;

  const queryStrings = buildQueryString(flat as any);
  const url = `/insights-consumption-report?${queryStrings}`;

  const { data } = await apiGateway.get(url);

  return data;
}

export function useReportRequest(filters: ReportExportButtonProps["filters"]) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const animatingRef = useRef(false);
  const [translate] = useTranslation(["insights"]);
  const reset = useCallback(() => {
    setIsGenerating(false);
    setProgress(0);
    setCurrentStep("");
    setData(null);
    setError(null);
  }, []);

  const start = useCallback(async () => {
    setIsGenerating(true);
    setProgress(0);
    setCurrentStep("");
    setError(null);

    const inc = 100 / STEPS.length;

    const animate = (async () => {
      animatingRef.current = true;
      let total = 0;
      for (let i = 0; i < STEPS.length; i++) {
        setCurrentStep(translate(STEPS[i].label) as string);
        const startVal = total;
        const endVal = (i + 1) * inc;
        const ticks = 20;
        for (let j = 0; j < ticks; j++) {
          await new Promise((r) => setTimeout(r, STEPS[i].duration / ticks));
          setProgress(startVal + ((endVal - startVal) * (j + 1)) / ticks);
        }
        total = endVal;
      }
      animatingRef.current = false;
    })();

    try {
      const response = await fetchReportData(filters);
      setData(response);
      await animate;
      setIsGenerating(false);
    } catch (e: any) {
      setError(e?.message ?? "Erro desconhecido");
      setIsGenerating(false);
      setCurrentStep("Falha ao gerar o relatório.");
    }
  }, [filters]);

  return {
    isGenerating,
    progress,
    currentStep,
    data,
    error,
    start,
    reset,
  } as const;
}
